import { BaseApiClient, ApiResponse } from './base.client';
import type {
  NearbySportsParams,
  NearbySportsResponse,
  NearbyShowsParams,
  NearbyShowsResponse,
  NearbyHotelsParams,
  NearbyHotelsResponse,
  NearbyRestaurantsParams,
  NearbyRestaurantsResponse,
} from './restaurants.client';

export interface GeoData {
  country?: string;
  countryCode?: string;
  currency?: string;
  timezone?: string;
  city?: string;
  region?: string;
  latitude?: number;
  longitude?: number;
}

export interface CurrencyItem {
  CurrencyCode: string;
  CurrencyName: string;
  CurrencySymbol: string;
  CountryName?: string;
  IsMain: boolean;
}

/** Normalised geolocation from `GET /geo/auto-detect-city` — a thin
 *  proxy around ipwho.org that keeps the API key server-side. Any
 *  field can be `null` when the vendor didn't resolve it. */
export interface GeoLocation {
  ip: string;
  city: string | null;
  region: string | null;
  country: string | null;
  countryCode: string | null;
  latitude: number | null;
  longitude: number | null;
}

/** Envelope shape returned by the endpoint. `success: false` +
 *  `data: null` covers every failure path (upstream 5xx, network
 *  timeout, ipwho "no match", missing/private client IP) — callers
 *  branch on `resp.data` and fall back to their existing default. */
export interface GeoAutoDetectResponse {
  success: boolean;
  data: GeoLocation | null;
}
/** Canonical country record served by `GET /countries`. */
export interface CountryItem {
  /** ISO 3166-1 alpha-2 code, e.g. "GB". */
  code: string;
  /** English country name. */
  name: string;
  /** E.164 dial code prefixed with "+", e.g. "+44". */
  dialCode: string;
}

export class CommonClient extends BaseApiClient {
  constructor(baseUrl?: string) {
    // Env-driven base URL — no static fallback. BaseApiClient defers
    // the missing-env error to first `request()`, so importing the
    // package is safe even without env configured (e.g. during builds).
    super(baseUrl || process.env.NEXT_PUBLIC_COMMON_API_URL);
  }

  async getCurrencyList(): Promise<ApiResponse<CurrencyItem[]>> {
    return this.get<ApiResponse<CurrencyItem[]>>('/currency/list');
  }

  async getCurrencyByCountry(country: string): Promise<ApiResponse<CurrencyItem>> {
    return this.get<ApiResponse<CurrencyItem>>('/currency/by-country', { country });
  }

  async getCurrencyByCode(code: string): Promise<ApiResponse<CurrencyItem>> {
    return this.get<ApiResponse<CurrencyItem>>('/currency/by-code', { code });
  }

  async getExchangeRates(
    target: string,
  ): Promise<ApiResponse<{ base: string; rates: Record<string, number> }>> {
    return this.get<ApiResponse<{ base: string; rates: Record<string, number> }>>(
      '/currency/exchange-rates',
      { target },
    );
  }

  /**
   * Auto-detect the caller's coarse geolocation via the common-service
   * ipwho.org proxy. Envelope is always shape-stable — check
   * `resp.data` (falsy on any failure) before consuming.
   */
  async autoDetectCity(): Promise<GeoAutoDetectResponse> {
    return this.get<GeoAutoDetectResponse>('/geo/auto-detect-city');
  }
  async getGeo(): Promise<ApiResponse<GeoData | null>> {
    return this.get<ApiResponse<GeoData | null>>('/geo');
  }

  /** Canonical ISO 3166-1 alpha-2 country list with English names + dial codes. */
  async getCountries(): Promise<ApiResponse<CountryItem[]>> {
    return this.get<ApiResponse<CountryItem[]>>('/countries');
  }

  // ── Cross-sell "Nearby X" hub ──────────────────────────────────────
  // All five categories go through common-service — it fans out to the
  // domain service that owns each dataset. Response shapes are the
  // per-category shapes today's frontend already reads (imported from
  // `restaurants.client.ts` so there's a single source of truth).

  /** GET /nearby/hotels — proxied to `hotels-service /search/nearby-hotels`. */
  async getNearbyHotels(params: NearbyHotelsParams): Promise<NearbyHotelsResponse> {
    return this.get<NearbyHotelsResponse>('/nearby/hotels', params as unknown as Record<string, any>);
  }

  /** GET /nearby/sports — proxied to `sports-events-service /nearby-sports`. */
  async getNearbySports(params: NearbySportsParams): Promise<NearbySportsResponse> {
    return this.get<NearbySportsResponse>('/nearby/sports', params as unknown as Record<string, any>);
  }

  /** GET /nearby/shows — proxied to sports-events-service's
   *  `/events/nearby-shows-geo` (or `/events/{id}/nearby-shows` when
   *  `eventId` is passed). The hub handles the branch — the client just
   *  forwards the whole param bag. */
  async getNearbyShows(
    params: NearbyShowsParams & { eventId?: number },
  ): Promise<NearbyShowsResponse> {
    return this.get<NearbyShowsResponse>('/nearby/shows', params as unknown as Record<string, any>);
  }

  /** GET /nearby/restaurants — proxied to `restaurants-service /nearby-restaurants`. */
  async getNearbyRestaurants(
    params: NearbyRestaurantsParams,
  ): Promise<NearbyRestaurantsResponse> {
    return this.get<NearbyRestaurantsResponse>(
      '/nearby/restaurants',
      params as unknown as Record<string, any>,
    );
  }

  /** GET /nearby/concerts — proxied to `concerts-service /nearby-concerts`.
   *  Same response shape as sports (concerts reuses `getNearbySports`
   *  upstream, filtered to `eventType.concert: true`). */
  async getNearbyConcerts(params: NearbySportsParams): Promise<NearbySportsResponse> {
    return this.get<NearbySportsResponse>(
      '/nearby/concerts',
      params as unknown as Record<string, any>,
    );
  }
}

export const commonClient = new CommonClient();
